import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Loading, Msg, Section, Select, panelPost, useAdminGet } from '../kit';
import { str } from '../ipintel/shared';
import PageTransition, { BreadcrumbsToggle } from './PageTransition';
import { SimilarTechSettings, TechCardDesign, TrlMessaging, UniversityPagesSettings } from './CardSettings';
import {
  CTAS, CTA_LABELS, DEFAULT_CTAS, DEFAULT_SECTIONS, MODES, MODE_LABELS, type Matrix, S, SECTIONS, SECTION_HINTS,
  SECTION_LABELS, SENSITIVE_SECTIONS, STATES, STATE_HINTS, STATE_LABELS, SUMMARY_MODES,
} from './shared';

/**
 * Sections & access — which section each visitor state sees, and how:
 * visible, locked (blurred filler + a sign-in / upgrade button; the server
 * sends NO data for it) or hidden (left out). The summary also has short
 * (first two sentences) and full. Plus which CTAs each state gets.
 * Reads GET tech_pages/settings and saves POST tech_pages/settings_save
 * {sections, ctas} — the server enforces it on every request, so a locked
 * section cannot be scraped from the API either. Below: the platform's tech
 * card design (accordion / legacy) and the TRL block wording (CardSettings.tsx).
 */

type Shape = 'section' | 'state' | 'flat';

/** Accepts {row: {state: v}}, {state: {row: v}} or {row: v}; returns the row-major matrix + the shape it came in. */
const normMatrix = (v: any, rows: readonly string[], defaults: Matrix): { m: Matrix; shape: Shape } => {
  const stateMajor = !!v && STATES.some(s => v[s] && typeof v[s] === 'object');
  const flat = !!v && !stateMajor && rows.some(r => typeof v[r] === 'string');
  const m: Matrix = {};
  rows.forEach(r => {
    m[r] = {};
    STATES.forEach(s => {
      const raw = stateMajor ? v?.[s]?.[r] : flat ? v?.[r] : v?.[r]?.[s];
      m[r][s] = str(raw) || defaults[r]?.[s] || '';
    });
  });
  return { m, shape: stateMajor ? 'state' : flat ? 'flat' : 'section' };
};

const denorm = (m: Matrix, shape: Shape): any => {
  if (shape === 'flat') return Object.fromEntries(Object.entries(m).map(([r, v]) => [r, v.pre_login]));
  if (shape === 'state') return Object.fromEntries(STATES.map(s => [s, Object.fromEntries(Object.keys(m).map(r => [r, m[r][s]]))]));
  return m;
};

// visible / full are the same thing for the summary; the matrix shows "full".
const shownMode = (section: string, mode: string) => (section === 'summary' && mode === 'visible' ? 'full' : mode);
const RANK: Record<string, number> = { hidden: 0, locked: 1, short: 2, visible: 3, full: 3 };

const SectionsAccess: React.FC = () => {
  const res = useAdminGet(S, 'settings', {}, []);
  const [sections, setSections] = useState<Matrix>(DEFAULT_SECTIONS);
  const [ctas, setCtas] = useState<Matrix>(DEFAULT_CTAS);
  const [shapes, setShapes] = useState<{ sections: Shape; ctas: Shape }>({ sections: 'section', ctas: 'section' });
  const [base, setBase] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = (data: any) => {
    const s = normMatrix(data?.sections, SECTIONS, DEFAULT_SECTIONS);
    const c = normMatrix(data?.ctas, CTAS, DEFAULT_CTAS);
    Object.keys(s.m).forEach(r => STATES.forEach(st => { s.m[r][st] = shownMode(r, s.m[r][st]); }));
    setSections(s.m); setCtas(c.m); setShapes({ sections: s.shape, ctas: c.shape });
    setBase(JSON.stringify([s.m, c.m]));
  };
  useEffect(() => { if (res.data) load(res.data); }, [res.data]);

  const dirty = base !== '' && JSON.stringify([sections, ctas]) !== base;
  const saved = useMemo<Matrix>(() => { try { return JSON.parse(base)[0] || {}; } catch { return {}; } }, [base]);
  const setMode = (sec: string, st: string, v: string) => setSections(m => ({ ...m, [sec]: { ...m[sec], [st]: v } }));
  const setCta = (cta: string, st: string, on: boolean) => setCtas(m => {
    const val = on ? 'shown' : 'hidden';
    const row = shapes.ctas === 'flat' ? Object.fromEntries(STATES.map(s => [s, val])) : { ...m[cta], [st]: val };
    return { ...m, [cta]: row };
  });

  const warnings = useMemo(() => {
    const w: string[] = [];
    SECTIONS.forEach(sec => {
      const m = sections[sec] || {};
      const name = SECTION_LABELS[sec];
      if (SENSITIVE_SECTIONS.has(sec) && ['visible', 'full', 'short'].includes(m.pre_login)) {
        w.push(`${name} is ${m.pre_login === 'short' ? 'shown (short)' : 'shown'} before sign-in — that text becomes public, crawlable and scrapeable.`);
      }
      if (m.paid === 'locked') w.push(`${name} is locked for paid users — nobody can unlock it (there is no plan above paid).`);
      if ((RANK[m.pre_login] ?? 0) > (RANK[m.post_login] ?? 0)) w.push(`${name}: signed-in visitors see less than signed-out ones.`);
      if ((RANK[m.post_login] ?? 0) > (RANK[m.paid] ?? 0)) w.push(`${name}: paid users see less than free signed-in ones.`);
    });
    return w;
  }, [sections]);

  const save = async () => {
    setBusy(true); setMsg(null);
    const body = { sections: denorm(sections, shapes.sections), ctas: denorm(ctas, shapes.ctas) };
    const { data, error } = await panelPost(S, 'settings_save', body);
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    if (data && typeof data === 'object') load(data);
    setMsg({ kind: 'ok', text: 'Saved. Pages already in the server cache pick it up within about 5 minutes.' });
  };

  const example = (Array.isArray(res.data?.examples) ? res.data.examples : []).find((e: any) => str(e?.kind) === 'tech');
  const exPath = str(example?.path);
  const th = 'text-left font-grotesk text-xs px-3 py-2 whitespace-nowrap bg-grayscale-200 text-grayscale-1200';
  const ctaStates = shapes.ctas === 'flat' ? ['pre_login'] : [...STATES];

  if (res.loading && !res.data) return <Loading label="Reading the page settings…" />;
  return (
    <div className="flex flex-col gap-4">
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <Section title="Sections × visitor state"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {dirty && <span className="text-[11px] text-amber-700">unsaved changes</span>}
            <Btn small onClick={() => { setSections(DEFAULT_SECTIONS); setCtas(DEFAULT_CTAS); }} disabled={busy}
              title="Put the launch defaults in the matrix (still needs Save)">Reset to defaults</Btn>
            <Btn small onClick={() => res.data && load(res.data)} disabled={busy || !dirty}>Discard changes</Btn>
            <Btn small kind="primary" onClick={save} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save'}</Btn>
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          <b>Visible</b> sends the section. <b>Locked</b> sends no data at all — the page shows blurred filler with
          “Sign up to view” (pre-login) or “Upgrade to unlock” (post-login). <b>Hidden</b> leaves it out. The server
          applies this to every request, so what is locked or hidden cannot be read from the API either.
        </p>
        <div className="overflow-auto rounded-lg border border-grayscale-300">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={th}>Section</th>
                {STATES.map(s => <th key={s} className={th} title={STATE_HINTS[s]}>{STATE_LABELS[s]}</th>)}
              </tr>
            </thead>
            <tbody>
              {SECTIONS.map(sec => (
                <tr key={sec} className="border-t border-grayscale-200 align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-grayscale-1300">{SECTION_LABELS[sec]}</div>
                    <div className="text-[11px] text-grayscale-1100 max-w-[22rem]">{SECTION_HINTS[sec]}</div>
                  </td>
                  {STATES.map(st => {
                    const v = sections[sec]?.[st] || '';
                    const opts = sec === 'summary' ? SUMMARY_MODES : MODES;
                    const changed = !!base && saved[sec]?.[st] !== v;
                    return (
                      <td key={st} className={`px-3 py-2 ${changed ? 'bg-amber-50' : ''}`}>
                        <Select value={opts.includes(v) ? v : ''} onChange={x => setMode(sec, st, x)}
                          options={opts.includes(v) ? opts : ['', ...opts]}
                          labels={{ ...MODE_LABELS, '': v ? `${v} (unknown)` : '—' }} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {warnings.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex flex-col gap-0.5">
            {warnings.map(w => <div key={w}>⚠ {w}</div>)}
          </div>
        )}
      </Section>

      <Section title="CTAs × visitor state">
        <p className="text-xs text-grayscale-1100 -mt-1">
          Untick to hide a button for that state. Contact University opens the login for a signed-out visitor and a
          next-steps panel (to Cosmos) for a signed-in one; Get IP Assessment goes to /ip-intel-report.
        </p>
        <div className="overflow-auto rounded-lg border border-grayscale-300">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={th}>Button</th>
                {ctaStates.map(s => (
                  <th key={s} className={th}>{shapes.ctas === 'flat' ? 'All states' : STATE_LABELS[s]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CTAS.map(c => (
                <tr key={c} className="border-t border-grayscale-200">
                  <td className="px-3 py-2 font-medium text-grayscale-1300">{CTA_LABELS[c]}</td>
                  {ctaStates.map(st => {
                    const on = (ctas[c]?.[st] || 'shown') !== 'hidden';
                    return (
                      <td key={st} className="px-3 py-2">
                        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                          <input type="checkbox" checked={on} onChange={e => setCta(c, st, e.target.checked)}
                            aria-label={`${CTA_LABELS[c]} — ${STATE_LABELS[st]}`} />
                          {on ? 'Shown' : 'Hidden'}
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <TechCardDesign data={res.data} />
      <TrlMessaging data={res.data} />
      <SimilarTechSettings data={res.data} />
      <UniversityPagesSettings data={res.data} />

      <PageTransition data={res.data} />
      <BreadcrumbsToggle data={res.data} />

      {exPath && (
        <Section title="Check it on a real page">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-mono text-grayscale-1100 break-all">{exPath}</span>
            {STATES.map(s => (
              <a key={s} href={`${exPath}?preview_state=${s}`} target="_blank" rel="noopener noreferrer"
                className="py-1 px-2.5 rounded-full border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200 whitespace-nowrap">
                As {STATE_LABELS[s].toLowerCase()} ↗
              </a>
            ))}
          </div>
          <div className="text-[11px] text-grayscale-1100">Shows the saved settings (save first). Works while you are signed in as an admin.</div>
        </Section>
      )}
    </div>
  );
};

export default SectionsAccess;
